import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { TrendingUp, Clock, DollarSign } from 'lucide-react';
import { caseStudiesData } from '../mockData';

const CaseStudies = () => {
  return (
    <section id="case-studies" className="py-24 bg-gradient-to-br from-slate-900 to-slate-800">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-cyan-400 text-slate-900 hover:bg-cyan-500 px-4 py-1.5 text-sm font-semibold">
            PROVEN RESULTS
          </Badge>
          <h2 className="text-5xl font-bold text-white mb-6">
            Real Businesses, Real Results
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            See how AI ASAP transformed operations and revenue for businesses just like yours
          </p>
        </div>

        {/* Case Studies */}
        <div className="space-y-8">
          {caseStudiesData.map((study, index) => (
            <Card 
              key={study.id}
              className="bg-white/95 backdrop-blur-sm border-2 hover:border-cyan-500 transition-all duration-300 overflow-hidden"
            >
              <div className="grid lg:grid-cols-3 gap-0">
                {/* Company Info */}
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-8 lg:p-10 border-r border-slate-200">
                  <Badge className="mb-4 bg-blue-600 text-white hover:bg-blue-700 px-3 py-1">
                    {study.industry}
                  </Badge>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">
                    {study.company}
                  </h3>
                  <p className="text-slate-600 mb-6">{study.location}</p>
                  
                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-slate-700 mb-2">THE CHALLENGE:</h4>
                    <p className="text-slate-700 leading-relaxed">{study.challenge}</p>
                  </div>

                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-slate-700 mb-2">THE SOLUTION:</h4>
                    <p className="text-slate-700 leading-relaxed">{study.solution}</p>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Clock className="h-4 w-4" />
                    <span className="font-semibold">{study.timeframe}</span>
                  </div>
                </div>

                {/* Results Metrics */}
                <div className="p-8 lg:p-10">
                  <h4 className="text-lg font-bold text-slate-900 mb-6">Key Results:</h4>
                  <div className="space-y-6">
                    {study.results.map((result, idx) => (
                      <div key={idx} className="border-l-4 border-cyan-500 pl-4">
                        <div className="text-sm font-semibold text-slate-600 mb-1">
                          {result.metric}
                        </div>
                        <div className="flex items-baseline gap-3 mb-1">
                          <span className="text-lg text-slate-500 line-through">{result.before}</span>
                          <span className="text-2xl font-bold text-slate-900">{result.after}</span>
                        </div>
                        <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded text-sm font-bold">
                          <TrendingUp className="h-3 w-3" />
                          {result.improvement}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Testimonial */}
                <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-8 lg:p-10 text-white flex flex-col justify-center">
                  <div className="text-5xl mb-4 opacity-50">"</div>
                  <blockquote className="text-lg leading-relaxed mb-6">
                    {study.testimonial}
                  </blockquote>
                  <div className="border-t border-white/30 pt-4">
                    <div className="font-bold text-lg">{study.author}</div>
                    <div className="text-cyan-100 text-sm">{study.company}</div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-xl text-white mb-4">
            Ready to see similar results in your business?
          </p>
          <a 
            href="https://calendly.com/aiasap-info/ai-asap-consultation"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-cyan-400 hover:bg-cyan-500 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
          >
            Get Your Free AI Audit
          </a>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
